import { Box, Link } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { type ReactNode, useMemo } from 'react';

import { LiftAITable } from '~/components/table';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import { EvidenceIcon } from '~/components/tasks/detail/taskSurfaces';
import { formatDate } from '~/components/tasks/taskStyle';
import type { TaskDetail, TaskEvidence, TaskLog } from '~/data/generated/model';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';

const CENTERED_CELL = { display: 'flex', alignItems: 'center', height: '100%' } as const;

type ActivityRow = TaskLog & { system: boolean; files: TaskEvidence[] };

const SYSTEM_STYLE = { label: 'System', bg: '#f1f3f6', fg: '#5f6b78' };

function Pill({ style }: { style: { label: string; bg: string; fg: string } }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 11,
        px: '8px',
        py: '2px',
        borderRadius: '10px',
        bgcolor: style.bg,
        color: style.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {style.label}
    </Box>
  );
}

export function ActivityTable({
  task,
  systemIds,
  hideSystem,
  chipStyle,
  toolbarStart,
}: {
  task: TaskDetail;
  systemIds: Set<string>;
  hideSystem: boolean;
  chipStyle: (log: TaskLog) => { label: string; bg: string; fg: string };
  toolbarStart?: ReactNode;
}) {
  const apiRef = useGridApiRef();
  const tableHeight = useDetailTableHeight();

  const rows = useMemo<ActivityRow[]>(
    () =>
      task.logs
        .filter((log) => !hideSystem || !systemIds.has(log.id))
        .map((log) => ({
          ...log,
          system: systemIds.has(log.id),
          files: task.evidence.filter((item) => item.logId === log.id),
        })),
    [task.logs, task.evidence, systemIds, hideSystem],
  );

  const columns = useMemo<LiftAIGridColDef<ActivityRow>[]>(
    () => [
      {
        field: 'createdAt',
        headerName: 'Date',
        width: 130,
        type: 'dateTime',
        valueGetter: (value: string) => new Date(value),
        renderCell: ({ row }) => <Box sx={CENTERED_CELL}>{formatDate(row.createdAt)}</Box>,
      },
      {
        field: 'label',
        headerName: 'Label',
        width: 150,
        valueGetter: (_value, row) => (row.system ? SYSTEM_STYLE.label : chipStyle(row).label),
        renderCell: ({ row }) => (
          <Box sx={CENTERED_CELL}>
            <Pill style={row.system ? SYSTEM_STYLE : chipStyle(row)} />
          </Box>
        ),
      },
      {
        field: 'text',
        headerName: 'Update',
        flex: 1,
        minWidth: 260,
        renderCell: ({ row }) => (
          <Box
            sx={{
              py: '10px',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.5,
              color: row.system ? '#6b7480' : '#1b1f24',
              fontSize: row.system ? 12 : 14,
            }}
          >
            {row.text}
          </Box>
        ),
      },
      {
        field: 'author',
        headerName: 'Author',
        width: 160,
        valueGetter: (_value, row) => row.author?.name ?? (row.system ? 'System' : 'Unknown user'),
        renderCell: ({ value }) => <Box sx={CENTERED_CELL}>{value}</Box>,
      },
      {
        field: 'files',
        headerName: 'Evidence',
        width: 220,
        sortable: false,
        valueGetter: (_value, row) => row.files.map((item) => item.name).join(', '),
        renderCell: ({ row }) =>
          row.files.length === 0 ? null : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', py: '8px' }}>
              {row.files.map((item) => (
                <Box
                  key={item.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 13 }}
                >
                  <EvidenceIcon fileType={item.fileType} size={18} />
                  <Link
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: '#1b1f24', wordBreak: 'break-word' }}
                  >
                    {item.name}
                  </Link>
                </Box>
              ))}
            </Box>
          ),
      },
    ],
    [chipStyle],
  );

  return (
    <LiftAITable<ActivityRow>
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      rowCount={rows.length}
      height={tableHeight}
      tableHeaderTitle="Activity"
      toolbarProps={{ beforeSearch: toolbarStart }}
      getRowHeight={() => 'auto'}
      disableRowSelectionOnClick
    />
  );
}
